<script setup lang="ts">
/**
 * Single-line text input.
 *
 * Defaults to a 44px-tall touch target with 16px font so iOS doesn't zoom
 * on focus. Pass `size="sm"` only on desktop-only surfaces.
 */
import { computed, useAttrs } from 'vue'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/shared/lib/cn'

const input = cva(
  [
    'flex w-full rounded-md bg-background text-foreground',
    'border border-input',
    'placeholder:text-muted-foreground',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
    'disabled:opacity-50 disabled:cursor-not-allowed',
    'aria-[invalid=true]:border-destructive aria-[invalid=true]:focus-visible:ring-destructive',
    // Allow long pasted values to fit.
    'overflow-ellipsis',
  ],
  {
    variants: {
      size: {
        // 16px font everywhere; height adapts.
        sm: 'h-9 px-3 text-base md:text-sm',
        md: 'h-11 px-3 text-base md:text-sm',
        lg: 'h-12 px-4 text-base',
      },
    },
    defaultVariants: { size: 'md' },
  },
)

type InputVariants = VariantProps<typeof input>

interface Props {
  modelValue?: string | number | null
  size?: InputVariants['size']
  invalid?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: '',
  size: 'md',
  invalid: false,
})

const emit = defineEmits<{ 'update:modelValue': [value: string] }>()

const attrs = useAttrs()
const classes = computed(() => cn(input({ size: props.size }), (attrs.class as string) ?? ''))

function onInput(e: Event) {
  const target = e.target as HTMLInputElement
  emit('update:modelValue', target.value)
}
</script>

<template>
  <input
    :value="modelValue ?? ''"
    :aria-invalid="invalid || undefined"
    data-focus-visible-managed
    :class="classes"
    @input="onInput"
  />
</template>
